import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "../lib/api";

/**
 * FeaturedProducts — pinned product strip rendered on the homepage or wholesale
 * catalogue. Reads CMS `featured.home` / `featured.wholesale` (list of product
 * ids) and renders each in a compact card. Falls back to nothing if no ids are
 * pinned (no layout shift, no orphan headings).
 */
export default function FeaturedProducts({ scope = "home", title = "Featured", subtitle = "" }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cms = (await api.get("/cms")).data || {};
        const ids = (cms.featured && cms.featured[scope]) || [];
        if (!ids.length) { setProducts([]); return; }
        // Pull whichever catalogue this scope belongs to (retail for home, wholesale for wholesale)
        const endpoint = scope === "wholesale" ? "/wholesale/products" : "/products";
        let all = [];
        try { all = (await api.get(endpoint)).data || []; } catch { all = []; }
        const byId = Object.fromEntries(all.map((p) => [p.id, p]));
        const picked = ids.map((id) => byId[id]).filter(Boolean);
        if (alive) setProducts(picked);
      } catch {
        if (alive) setProducts([]);
      }
    })();
    return () => { alive = false; };
  }, [scope]);

  if (!products.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid={`featured-${scope}`}>
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">
            <Star size={12} /> {title}
          </div>
          {subtitle && <p className="text-stone-600 mt-2 max-w-xl">{subtitle}</p>}
        </div>
        <Link
          to={scope === "wholesale" ? "/wholesale/products" : "/products"}
          className="hidden sm:inline-flex text-sm font-bold text-[#166534] hover:underline"
        >
          See all →
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid={`featured-${scope}-grid`}>
        {products.map((p) => (
          <Link
            key={p.id}
            to={scope === "wholesale" ? "/wholesale/products" : "/products"}
            className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all"
            data-testid={`featured-${scope}-${p.id}`}
          >
            <div className="h-44 bg-stone-100 overflow-hidden">
              {p.image_url && (
                <img
                  src={p.image_url}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
            </div>
            <div className="p-4">
              <div className="font-display font-bold text-stone-900 leading-tight">{p.name}</div>
              {p.pack_label && <div className="text-xs text-stone-500 mt-0.5">{p.pack_label}</div>}
              {p.price_excl_gst == null ? (
                <div className="mt-2 text-xs font-bold text-[#166534]" data-testid={`feat-price-hidden-${p.id}`}>
                  Log in to view price →
                </div>
              ) : (
                <div className="mt-2 font-display font-black text-xl text-[#EA580C]">
                  ${Number(p.price_excl_gst).toFixed(2)}
                  <span className="text-[10px] font-bold text-stone-400 ml-1">excl GST</span>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
